import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { AdminGuard, AdminNav } from "../components/admin/AdminShell";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Truck, ChatCircle } from "@phosphor-icons/react";

// SHIPPING PRICE IS NOT ADMIN-CONFIGURABLE.
// Shipping is INCLUDED in the product price on every order and Stripe
// Checkout receives no shipping_options. The knobs surfaced here are:
//   * Delivery-window ETA (min/max business days) -- used by the
//     /payment/success ETA card and owner emails.
//   * Marketing tags (GA4, Google Ads Purchase conversion label).
//   * Stripe Tax toggle.
const DELIVERY_ETA_FIELDS = [
  { key: "standard_min_days", label: "Delivery ETA min days", type: "number" },
  { key: "standard_max_days", label: "Delivery ETA max days", type: "number" },
];

const MARKETING_FIELDS = [
  { key: "ga4_measurement_id",
    label: "GA4 Measurement ID",
    placeholder: "G-XXXXXXXXXX",
    hint: "Populates gtag config on every non-admin page." },
  { key: "google_ads_purchase_conversion_label",
    label: "Google Ads Purchase conversion label",
    placeholder: "AbCdEfGh_IjKlMn",
    hint: "OPTIONAL. Leave blank if using URL-based page-load conversion. When set, /payment/success also fires gtag('event','conversion',{send_to:'AW-18375113165/<label>'})." },
];

export default function AdminShippingSettings() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({});

  useEffect(() => {
    api.get("/admin/site-settings").then((r) => { setSettings(r.data); setDraft(r.data); }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const patch = {};
      DELIVERY_ETA_FIELDS.forEach((f) => {
        if (draft[f.key] != null && draft[f.key] !== settings[f.key]) patch[f.key] = parseInt(draft[f.key], 10);
      });
      MARKETING_FIELDS.forEach((f) => {
        if (draft[f.key] != null && draft[f.key] !== settings[f.key]) patch[f.key] = draft[f.key] ?? "";
      });
      if (draft.stripe_tax_enabled !== settings.stripe_tax_enabled) patch.stripe_tax_enabled = draft.stripe_tax_enabled;
      if (Object.keys(patch).length === 0) { toast.info("Nothing to save"); return; }
      const { data } = await api.patch("/admin/site-settings", patch);
      setSettings(data);
      setDraft(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return (
    <AdminGuard><AdminNav /><div className="max-w-6xl mx-auto px-4 py-12 text-brand-muted">Loading…</div></AdminGuard>
  );

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <AdminGuard>
      <AdminNav />
      <div className="max-w-6xl mx-auto px-4 py-10" data-testid="admin-shipping-settings">
        <div className="overline mb-3">Administrator</div>
        <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-brand-navy">Delivery, Tax &amp; Marketing</h1>
        <p className="mt-3 text-sm text-brand-muted max-w-2xl">
          Shipping is <strong>included in the product price</strong> on every order — customers are not charged a separate shipping fee.  These controls only drive the delivery-window ETA, tax computation, and marketing tags.  Changes take effect immediately.
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-brand-border bg-white p-6" data-testid="shipping-tier-standard">
            <div className="overline mb-2 flex items-center gap-2"><Truck size={12} weight="duotone" className="text-brand-accent" /> Delivery window (ETA only)</div>
            {DELIVERY_ETA_FIELDS.map((f) => (
              <div key={f.key} className="mb-3">
                <Label className="text-[10px] text-brand-muted uppercase tracking-widest font-mono">{f.label}</Label>
                <Input
                  type="number"
                  value={draft[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  data-testid={`shipping-input-${f.key}`}
                  className="mt-1 bg-brand-surface border-brand-border text-brand-navy font-mono"
                />
              </div>
            ))}
            <p className="mt-3 text-[10px] text-brand-muted font-mono">
              Used ONLY for the delivery-window shown on /payment/success and in owner emails.  Shipping is included in the product price and Stripe Checkout receives no shipping line.
            </p>
          </div>

          <div className="border border-brand-border bg-white p-6">
            <div className="overline mb-2">Stripe Tax</div>
            <div className="flex items-center gap-3 mt-3">
              <Switch
                checked={!!draft.stripe_tax_enabled}
                onCheckedChange={(v) => setField("stripe_tax_enabled", v)}
                data-testid="shipping-stripe-tax-toggle"
              />
              <span className="text-sm text-brand-text">Enable automatic tax on Checkout Sessions</span>
            </div>
            <p className="mt-2 text-[10px] text-brand-muted font-mono">
              When enabled, Stripe computes destination-based sales tax at checkout using verified merchant address.
            </p>
          </div>
        </div>

        <div className="mt-8 border border-brand-border bg-white p-6" data-testid="shipping-marketing-block">
          <div className="overline mb-2 flex items-center gap-2"><ChatCircle size={12} weight="duotone" className="text-brand-accent" /> Marketing tags</div>
          {MARKETING_FIELDS.map((f) => (
            <div key={f.key} className="mb-4">
              <Label className="text-[10px] text-brand-muted uppercase tracking-widest font-mono">{f.label}</Label>
              <Input
                type="text"
                value={draft[f.key] ?? ""}
                placeholder={f.placeholder}
                onChange={(e) => setField(f.key, e.target.value)}
                data-testid={`marketing-input-${f.key}`}
                className="mt-1 bg-brand-surface border-brand-border text-brand-navy font-mono"
              />
              {f.hint && <p className="mt-1 text-[10px] text-brand-muted font-mono">{f.hint}</p>}
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-center gap-3">
          <button
            onClick={save}
            disabled={busy}
            data-testid="shipping-save-btn"
            className="bg-brand-navy text-white hover:bg-brand-navy2 font-semibold px-6 py-3 text-sm tracking-wide disabled:opacity-40"
          >
            {busy ? "Saving…" : "Save settings"}
          </button>
          <button
            onClick={() => setDraft(settings)}
            disabled={busy}
            className="border border-brand-border text-brand-text hover:bg-brand-surface px-4 py-3 text-xs uppercase tracking-widest font-mono"
            data-testid="shipping-reset-btn"
          >Reset</button>
        </div>
      </div>
    </AdminGuard>
  );
}
